import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminApi, DashboardStats, SystemHealthData, ConnectionNode } from '../utils/api';
import {
  UsersIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

// Constants
const DASHBOARD_REFETCH_INTERVAL_MS = 10000;
const ROUTES = {
  USERS: '/admin/users',
  SESSIONS: '/admin/sessions',
  LOGS: '/admin/logs',
} as const;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: stats, isLoading, error, dataUpdatedAt, isFetching } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await adminApi.getDashboardStats();
      return response.data;
    },
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });

  // Fetch system health data
  const { data: systemHealth, isLoading: isSystemHealthLoading, error: systemHealthError } = useQuery<SystemHealthData>({
    queryKey: ['system-health'],
    queryFn: async () => {
      const response = await adminApi.getSystemHealth();
      return response.data;
    },
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });

  // Fetch connection nodes
  const { data: connectionNodes, isLoading: isConnectionNodesLoading, error: connectionNodesError } = useQuery<ConnectionNode[]>({
    queryKey: ['connection-nodes'],
    queryFn: async () => {
      const response = await adminApi.getConnectionNodes();
      return response.data;
    },
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
  });

  // Calculate connection node statistics (optimized with useMemo)
  // Must be before early returns due to React Hooks rules
  const nodeStats = React.useMemo(() => {
    if (!connectionNodes || connectionNodes.length === 0) {
      return { total: 0, active: 0, unhealthy: 0, offline: 0, avgLoad: 0 };
    }

    let active = 0;
    let unhealthy = 0;
    let offline = 0;
    let totalLoad = 0;

    for (const node of connectionNodes) {
      totalLoad += node.load_percentage;
      switch (node.status) {
        case 'active':
          active++;
          break;
        case 'unhealthy':
          unhealthy++;
          break;
        case 'offline':
          offline++;
          break;
      }
    }

    return {
      total: connectionNodes.length,
      active,
      unhealthy,
      offline,
      avgLoad: Math.round(totalLoad / connectionNodes.length),
    };
  }, [connectionNodes]);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700">Failed to load dashboard data</span>
        </div>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: UsersIcon,
      color: 'bg-blue-500',
      change: `+${stats?.new_users_today || 0} today`,
      changeType: 'positive' as const,
    },
    {
      title: 'Active Users',
      value: stats?.active_users || 0,
      icon: CheckCircleIcon,
      color: 'bg-green-500',
      percentage: stats?.total_users ? Math.round((stats.active_users / stats.total_users) * 100) : 0,
    },
    {
      title: 'Total Sessions',
      value: stats?.total_sessions || 0,
      icon: ComputerDesktopIcon,
      color: 'bg-purple-500',
      change: `+${stats?.sessions_today || 0} today`,
      changeType: 'positive' as const,
    },
    {
      title: 'Active Sessions',
      value: stats?.active_sessions || 0,
      icon: ComputerDesktopIcon,
      color: 'bg-indigo-500',
      status: stats?.active_sessions ? 'active' : 'inactive',
    },
  ];

  const systemHealthStatus = stats?.system_health || 'unknown';
  const isHealthy = systemHealthStatus === 'healthy';

  // Helper function to format uptime
  const formatUptime = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Resource usage thresholds
  const USAGE_WARNING_THRESHOLD = 60;
  const USAGE_CRITICAL_THRESHOLD = 80;

  // Helper function to get color based on percentage
  const getPercentageColor = (percentage: number) => {
    if (percentage < USAGE_WARNING_THRESHOLD) return 'text-green-600';
    if (percentage < USAGE_CRITICAL_THRESHOLD) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Helper function to get progress bar color
  const getProgressBarColor = (percentage: number) => {
    if (percentage < USAGE_WARNING_THRESHOLD) return 'bg-green-500';
    if (percentage < USAGE_CRITICAL_THRESHOLD) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Helper function to get node status color
  const getNodeStatusColor = (status: ConnectionNode['status']): string => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-500',
      unhealthy: 'bg-yellow-500',
      offline: 'bg-red-500',
      draining: 'bg-blue-500',
      maintenance: 'bg-purple-500',
    };
    return colorMap[status] ?? 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard Overview
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Real-time system statistics and monitoring
          </p>
        </div>
        
        {/* System Health & Update Indicator */}
        <div className="mt-4 md:mt-0 md:ml-4 flex flex-col gap-2">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            isHealthy
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              isHealthy ? 'bg-green-400' : 'bg-red-400'
            }`} />
            System {systemHealthStatus}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            {isFetching && (
              <span className="inline-flex items-center">
                <svg className="animate-spin h-3 w-3 text-blue-500 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </span>
            )}
            {!isFetching && dataUpdatedAt && (
              <span>
                Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
              </span>
            )}
            <span className="text-gray-400">• Auto-refresh: {DASHBOARD_REFETCH_INTERVAL_MS / 1000}s</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="admin-card p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">
                    {stat.title}
                  </p>
                  <div className="flex items-baseline mt-2">
                    <p className="text-2xl font-semibold text-gray-900">
                      {stat.value.toLocaleString()}
                    </p>
                    {stat.percentage !== undefined && (
                      <p className="ml-2 text-sm text-gray-500">
                        ({stat.percentage}%)
                      </p>
                    )}
                  </div>
                  
                  {stat.change && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center text-sm ${
                        stat.changeType === 'positive' 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  
                  {stat.status && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        stat.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {stat.status}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* System Resources Monitor */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <CpuChipIcon className="h-5 w-5 mr-2 text-gray-600" />
          System Resources
        </h3>
        {systemHealthError ? (
          <div className="text-center py-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600">Failed to load system health data</p>
          </div>
        ) : isSystemHealthLoading ? (
          <div className="text-center py-4 text-gray-500">Loading system resources...</div>
        ) : systemHealth ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* CPU Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">CPU Usage</span>
                <span className={`text-sm font-semibold ${getPercentageColor(systemHealth.cpu_usage_percent)}`}>
                  {systemHealth.cpu_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(systemHealth.cpu_usage_percent)}`}
                  style={{ width: `${Math.min(systemHealth.cpu_usage_percent, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Memory Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Memory Usage</span>
                <span className={`text-sm font-semibold ${getPercentageColor(systemHealth.memory_usage_percent)}`}>
                  {systemHealth.memory_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(systemHealth.memory_usage_percent)}`}
                  style={{ width: `${Math.min(systemHealth.memory_usage_percent, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Disk Usage */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Disk Usage</span>
                <span className={`text-sm font-semibold ${getPercentageColor(systemHealth.disk_usage_percent)}`}>
                  {systemHealth.disk_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(systemHealth.disk_usage_percent)}`}
                  style={{ width: `${Math.min(systemHealth.disk_usage_percent, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Uptime and Infrastructure Status */}
        {systemHealth && (
          <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-xs text-gray-600 mb-1">System Uptime</div>
              <div className="text-sm font-semibold text-gray-900">
                {formatUptime(systemHealth.uptime_seconds)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600 mb-1">Database</div>
              <div className={`text-sm font-semibold ${systemHealth.database_healthy ? 'text-green-600' : 'text-red-600'}`}>
                {systemHealth.database_healthy ? 'Online' : 'Offline'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600 mb-1">Redis Cache</div>
              <div className={`text-sm font-semibold ${systemHealth.redis_healthy ? 'text-green-600' : 'text-red-600'}`}>
                {systemHealth.redis_healthy ? 'Online' : 'Offline'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-600 mb-1">Overall Status</div>
              <div className={`text-sm font-semibold ${systemHealth.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Connection Nodes Health */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <ServerStackIcon className="h-5 w-5 mr-2 text-gray-600" />
          Connection Nodes
        </h3>
        {connectionNodesError ? (
          <div className="text-center py-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600">Failed to load connection nodes</p>
          </div>
        ) : isConnectionNodesLoading ? (
          <div className="text-center py-4 text-gray-500">Loading connection nodes...</div>
        ) : connectionNodes && connectionNodes.length > 0 ? (
          <>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{nodeStats.total}</div>
              <div className="text-xs text-gray-600 mt-1">Total Nodes</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{nodeStats.active}</div>
              <div className="text-xs text-gray-600 mt-1">Active</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{nodeStats.unhealthy}</div>
              <div className="text-xs text-gray-600 mt-1">Unhealthy</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{nodeStats.offline}</div>
              <div className="text-xs text-gray-600 mt-1">Offline</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{nodeStats.avgLoad}%</div>
              <div className="text-xs text-gray-600 mt-1">Avg Load</div>
            </div>
          </div>

          {/* Top 5 Nodes */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Top Loaded Nodes</h4>
            <div className="space-y-2">
              {[...connectionNodes].sort((a, b) => b.load_percentage - a.load_percentage).slice(0, 5).map((node) => (
                <div key={node.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`w-2 h-2 rounded-full ${getNodeStatusColor(node.status)}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {node.region} - {node.host}
                      </div>
                      <div className="text-xs text-gray-500">
                        {node.city}, {node.country}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-xs text-gray-600">Load</div>
                      <div className={`text-sm font-semibold ${getPercentageColor(node.load_percentage)}`}>
                        {node.load_percentage.toFixed(0)}%
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-600">Capacity</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {node.current_load}/{node.capacity}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {connectionNodes.length > 5 && (
              <button
                onClick={() => navigate('/admin/connection-nodes')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View all {connectionNodes.length} nodes →
              </button>
            )}
          </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-500">No connection nodes available</div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate(ROUTES.USERS)}
            className="admin-button text-left p-4 flex items-center space-x-3 hover:bg-blue-50 transition-colors"
          >
            <UsersIcon className="h-5 w-5" />
            <span>Manage Users</span>
          </button>
          <button
            onClick={() => navigate(ROUTES.SESSIONS)}
            className="admin-button text-left p-4 flex items-center space-x-3 hover:bg-blue-50 transition-colors"
          >
            <ComputerDesktopIcon className="h-5 w-5" />
            <span>View Sessions</span>
          </button>
          <button
            onClick={() => navigate(ROUTES.LOGS)}
            className="admin-button text-left p-4 flex items-center space-x-3 hover:bg-blue-50 transition-colors"
          >
            <DocumentTextIcon className="h-5 w-5" />
            <span>Audit Logs</span>
          </button>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Today's Activity</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New User Registrations</span>
              <span className="text-sm font-medium text-gray-900">
                {stats?.new_users_today || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">VPN Sessions Started</span>
              <span className="text-sm font-medium text-gray-900">
                {stats?.sessions_today || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Currently Active Sessions</span>
              <span className="text-sm font-medium text-gray-900">
                {stats?.active_sessions || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="admin-card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Status</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Overall Health</span>
              <span className={`text-sm font-medium ${
                isHealthy ? 'text-green-600' : 'text-red-600'
              }`}>
                {systemHealthStatus.charAt(0).toUpperCase() + systemHealthStatus.slice(1)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Database</span>
              <span className={`text-sm font-medium ${
                !systemHealth ? 'text-gray-500'
                  : systemHealth.database_healthy ? 'text-green-600' : 'text-red-600'
              }`}>
                {!systemHealth
                  ? 'Unknown'
                  : systemHealth.database_healthy ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Redis Cache</span>
              <span className={`text-sm font-medium ${
                !systemHealth ? 'text-gray-500'
                  : systemHealth.redis_healthy ? 'text-green-600' : 'text-red-600'
              }`}>
                {!systemHealth
                  ? 'Unknown'
                  : systemHealth.redis_healthy ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">API Service</span>
              <span className={`text-sm font-medium ${
                systemHealth ? 'text-green-600'
                  : systemHealthError ? 'text-red-600' : 'text-gray-500'
              }`}>
                {systemHealth
                  ? 'Operational'
                  : systemHealthError ? 'Unavailable' : 'Unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;